import React, { useState, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Card, Button, Space, Input, List, Row, Col, Tag, Grid, Select, message, Pagination } from 'antd';
import { PlusOutlined, SearchOutlined, EyeOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { QueryDocumentSnapshot } from 'firebase/firestore';
import { DataTable, AntDColumn } from '@/components/DataTable';
import { ApartmentUnit } from '@/types/resources/property';
import { getApartmentUnits, addApartmentUnit, updateApartmentUnit, deleteApartmentUnit, getBlocks } from '@/api/properties';
import { ApartmentUnitFormModal } from '@/components/features/resources/properties/ApartmentUnitFormModal';
import { useAuth } from '@/contexts/AuthContext';
import DeleteConfirmationModal from '@/components/common/DeleteConfirmationModal';

const { useBreakpoint } = Grid;

// Constants
const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const API_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const ApartmentUnits: React.FC = () => {
  const { t } = useTranslation(['properties', 'common']);
  const navigate = useNavigate();
  const location = useLocation();
  const screens = useBreakpoint();
  const isMobile = !screens.md;
  const queryClient = useQueryClient();
  const { firestoreUser } = useAuth();
  
  // Check if this is the active tab
  const isActiveTab = location.pathname.includes('/units');
  
  // State
  const [searchText, setSearchText] = useState('');
  const [selectedBlock, setSelectedBlock] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedApartmentUnit, setSelectedApartmentUnit] = useState<ApartmentUnit | undefined>();
  const [formMode, setFormMode] = useState<'create' | 'edit'>('create');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [lastDocs, setLastDocs] = useState<QueryDocumentSnapshot[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [unitToDelete, setUnitToDelete] = useState<ApartmentUnit | null>(null);
  const [deleteError, setDeleteError] = useState<Error & { dependencies?: any[]; unitNumber?: string } | undefined>(undefined);
  const [blocksDropdownOpen, setBlocksDropdownOpen] = useState(false);

  // Queries
  const { data: unitsData = { apartmentUnits: [], lastDoc: undefined, total: 0 }, isLoading } = useQuery({
    queryKey: ['apartmentUnits', currentPage, pageSize, searchText, selectedBlock, selectedStatus, firestoreUser?.workspace?.id],
    queryFn: async () => {
      if (!firestoreUser) {
        throw new Error('User not authenticated');
      }
      const lastDoc = currentPage > 1 ? lastDocs[currentPage - 2] : undefined;
      
      console.log('Fetching apartment units:', { currentPage, pageSize, lastDoc, lastDocs: lastDocs.length });
      
      const result = await getApartmentUnits({
        searchTerm: searchText,
        pageSize,
        currentPage,
        lastDoc,
        firestoreUser
      });
      
      return result;
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: true,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    placeholderData: (previousData) => previousData,
    enabled: !!firestoreUser && isActiveTab, // Only run query if firestoreUser is available AND this is the active tab
  });

  const { data: blocksData = { blocks: [] }, isLoading: blocksLoading } = useQuery({
    queryKey: ['blocks', 'all', firestoreUser?.workspace?.id],
    queryFn: () => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return getBlocks({
        searchTerm: '',
        pageSize: 1000,
        firestoreUser
      });
    },
    staleTime: API_STALE_TIME_MS,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    retry: false,
    enabled: !!firestoreUser && isActiveTab && blocksDropdownOpen, // Only run query when dropdown is opened
  });

  // Update lastDocs when new data arrives
  React.useEffect(() => {
    if (unitsData.lastDoc) {
      setLastDocs(prev => {
        const newLastDocs = [...prev];
        newLastDocs[currentPage - 1] = unitsData.lastDoc!;
        return newLastDocs;
      });
    }
  }, [unitsData.lastDoc, currentPage]);

  // Reset lastDocs when page size changes
  React.useEffect(() => {
    setLastDocs([]);
    setCurrentPage(1);
  }, [pageSize]);

  // Reset to first page when filters change
  React.useEffect(() => {
    setCurrentPage(1);
    setLastDocs([]);
  }, [searchText, selectedBlock, selectedStatus, pageSize]);

  // Mutations
  const addMutation = useMutation({
    mutationFn: (unit: Omit<ApartmentUnit, 'id' | 'activityRecords' | 'workspace'>) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return addApartmentUnit(unit, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('apartmentUnits.messages.addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['apartmentUnits'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('apartmentUnits.messages.addError'));
      console.error('Error adding apartment unit:', error);
    }
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<ApartmentUnit> }) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return updateApartmentUnit(id, data, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('apartmentUnits.messages.updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['apartmentUnits'] });
      setIsModalVisible(false);
    },
    onError: (error) => {
      message.error(t('apartmentUnits.messages.updateError'));
      console.error('Error updating apartment unit:', error);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => {
      if (!firestoreUser) throw new Error('User not authenticated');
      return deleteApartmentUnit(id, firestoreUser);
    },
    onSuccess: () => {
      message.success(t('properties:apartmentUnits.messages.deleteSuccess'));
      queryClient.invalidateQueries({ queryKey: ['apartmentUnits'] });
      setShowDeleteModal(false);
      setDeleteError(undefined);
    },
    onError: (error: Error) => {
      if ((error as any).dependencies) {
        setDeleteError(error as any);
      } else {
        message.error(t('properties:apartmentUnits.messages.deleteError'));
        setShowDeleteModal(false);
      }
      console.error('Error deleting apartment unit:', error);
    }
  });

  // Handlers
  const handleCreate = useCallback(() => {
    setFormMode('create');
    setSelectedApartmentUnit(undefined);
    setIsModalVisible(true);
  }, []);

  const handleEdit = useCallback((apartmentUnit: ApartmentUnit) => {
    setFormMode('edit');
    setSelectedApartmentUnit(apartmentUnit);
    setIsModalVisible(true);
  }, []);

  const handleDelete = useCallback((apartmentUnit: ApartmentUnit) => {
    setUnitToDelete(apartmentUnit);
    setShowDeleteModal(true);
  }, []);

  const handleBulkDelete = useCallback((_: any, selectedRows: ApartmentUnit[]) => {
    // TODO: Implement bulk delete functionality
    console.log('Bulk delete units:', selectedRows);
  }, []);

  const handleSubmit = useCallback((values: Partial<ApartmentUnit>) => {
    if (selectedApartmentUnit?.id) {
      updateMutation.mutate({ id: selectedApartmentUnit.id, data: values });
    } else {
      addMutation.mutate(values as Omit<ApartmentUnit, 'id' | 'activityRecords' | 'workspace'>);
    }
  }, [selectedApartmentUnit, addMutation, updateMutation]);

  const handleViewDetails = useCallback((apartmentUnit: ApartmentUnit) => {
    navigate(`/resources/properties/units/${apartmentUnit.id}`, { state: { apartmentUnit } });
  }, [navigate]);

  const handlePageChange = useCallback((page: number, size?: number) => {
    setCurrentPage(page);
    if (size && size !== pageSize) {
      setPageSize(size);
    }
  }, [pageSize]);

  // Memoized values
  const filteredData = useMemo(() => {
    if (!unitsData.apartmentUnits) return [];
    // For demo mode, we'll do client-side filtering for block and status
    // In a real implementation, these would be passed to the API
    return unitsData.apartmentUnits.filter(unit => {
      const matchesBlock = !selectedBlock || unit.block?.id === selectedBlock;
      const matchesStatus = !selectedStatus || unit.status === selectedStatus;
      return matchesBlock && matchesStatus;
    });
  }, [unitsData.apartmentUnits, selectedBlock, selectedStatus]);

  const uniqueBlocks = useMemo(() => 
    blocksData.blocks.map(block => ({ value: block.id, label: block.name })), 
    [blocksData.blocks]
  );

  const columns: AntDColumn<ApartmentUnit>[] = [
    {
      title: t('apartmentUnits.details.attributes.number'),
      dataIndex: 'number' as keyof ApartmentUnit,
      key: 'number'
    },
    {
      title: t('apartmentUnits.details.attributes.block'),
      dataIndex: ['block', 'name'] as const,
      key: 'block'
    },
    {
      title: t('apartmentUnits.details.attributes.floor'),
      dataIndex: ['floor', 'name'] as const,
      key: 'floor'
    },
    {
      title: t('apartmentUnits.details.attributes.bedrooms'),
      dataIndex: 'bedrooms' as keyof ApartmentUnit,
      key: 'bedrooms'
    },
    {
      title: t('apartmentUnits.details.attributes.bathrooms'),
      dataIndex: 'bathrooms' as keyof ApartmentUnit,
      key: 'bathrooms'
    },
    {
      title: t('apartmentUnits.details.attributes.area'),
      dataIndex: 'area' as keyof ApartmentUnit,
      key: 'area',
      render: (area: number) => `${area} ${t('common:sqft')}`
    },
    {
      title: t('apartmentUnits.details.attributes.status'),
      dataIndex: 'status' as keyof ApartmentUnit,
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'occupied' ? 'green' : status === 'maintenance' ? 'orange' : 'blue'}>
          {t(`apartmentUnits.status.${status}`)}
        </Tag>
      )
    }
  ];

  const renderUnitCard = (unit: ApartmentUnit) => (
    <Card
      key={unit.id}
      hoverable
      style={{ marginBottom: 16 }}
      actions={[
        <Button
          key="view"
          type="link"
          icon={<EyeOutlined />}
          onClick={() => handleViewDetails(unit)}
        >
          {t('common:actions.view')}
        </Button>,
        <Button
          key="edit"
          type="link"
          icon={<EditOutlined />}
          onClick={() => handleEdit(unit)}
        >
          {t('common:actions.edit')}
        </Button>,
        <Button
          key="delete"
          type="link"
          icon={<DeleteOutlined />}
          danger
          onClick={() => handleDelete(unit)}
        >
          {t('common:actions.delete')}
        </Button>,
      ]}
    >
      <Card.Meta
        title={unit.number}
        description={
          <Space direction="vertical" size="small">
            <div>{unit.block?.name} - {unit.floor?.name}</div>
            <div>{unit.bedrooms} beds, {unit.bathrooms} baths</div>
            <div>{unit.area} {t('common:sqft')}</div>
            <div>
              <Tag color={unit.status === 'occupied' ? 'green' : unit.status === 'maintenance' ? 'orange' : 'blue'}>
                {t(`apartmentUnits.status.${unit.status}`)}
              </Tag>
            </div>
          </Space>
        }
      />
    </Card>
  );

  return (
    <div style={{ width: '100%' }}>
      {isMobile ? (
        <>
          <List
            dataSource={filteredData}
            renderItem={renderUnitCard}
            rowKey="id"
            grid={{ gutter: 16, xs: 1, sm: 1 }}
            header={
              <>
                <Row className="search-filter-section" gutter={[{ xs: 8, sm: 16 }, { xs: 8, sm: 16 }]} style={{ marginBottom: 16 }} align="middle">
                  <Col xs={24} sm={16} md={18}>
                    <Input
                      className="search-input"
                      id="units-search-input"
                      placeholder={t('common:ui.search')}
                      value={searchText}
                      onChange={(e) => setSearchText(e.target.value)}
                      prefix={<SearchOutlined />}
                      allowClear
                    />
                  </Col>
                  <Col xs={24} sm={8} md={6} style={{ textAlign: screens.xs ? 'left' : 'right', marginTop: screens.xs ? 8 : 0 }}>
                    <Button className="add-record-button" id="units-add-button" type="primary" onClick={handleCreate} icon={<PlusOutlined />} block={screens.xs}>
                      {t('apartmentUnits.actions.add')}
                    </Button>
                  </Col>
                </Row>
                <div style={{ 
                  marginBottom: 16,
                  overflowX: 'auto',
                  whiteSpace: 'nowrap',
                  display: 'flex',
                  gap: '8px'
                }}>
                  <Select
                    placeholder={t('apartmentUnits.fields.block')}
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    onOpenChange={setBlocksDropdownOpen}
                    loading={blocksLoading}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={uniqueBlocks}
                  />
                  <Select
                    placeholder={t('apartmentUnits.fields.status')}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ minWidth: 120 }}
                    options={[
                      { value: 'vacant', label: t('apartmentUnits.status.vacant') },
                      { value: 'occupied', label: t('apartmentUnits.status.occupied') },
                      { value: 'maintenance', label: t('apartmentUnits.status.maintenance') }
                    ]}
                  />
                </div>
              </>
            }
            loading={isLoading}
            pagination={false}
          />
          <div className="pagination-section" style={{ 
            marginTop: 16, 
            textAlign: 'center',
            padding: '0 8px'
          }}>
            <Pagination
              current={currentPage}
              pageSize={pageSize}
              total={unitsData.total}
              onChange={handlePageChange}
              showSizeChanger={!screens.xs}
              pageSizeOptions={PAGE_SIZE_OPTIONS}
              showQuickJumper={!screens.xs}
              showTotal={!screens.xs ? (total: number, range: [number, number]) => 
                t('common:pagination.showing', { 
                  start: range[0], 
                  end: range[1], 
                  total 
                }) : undefined
              }
              size={screens.xs ? 'small' : 'default'}
              simple={screens.xs}
              style={{ 
                display: 'flex', 
                justifyContent: 'center',
                flexWrap: 'wrap',
                gap: '8px'
              }}
            />
          </div>
        </>
      ) : (
        <>
          <Card className="data-table-card">
            <DataTable
              className="data-table"
              dataSource={filteredData}
              columns={columns}
              loading={isLoading}
              rowKey="id"
              onEdit={handleEdit}
              onDelete={handleDelete}
              onView={handleViewDetails}
              onBulkDelete={handleBulkDelete}
              pagination={{
                current: currentPage,
                pageSize: pageSize,
                total: unitsData.total,
                onChange: handlePageChange,
                showSizeChanger: true,
                showQuickJumper: true,
                pageSizeOptions: PAGE_SIZE_OPTIONS.map(String),
                showTotal: (total: number, range: [number, number]) => 
                  t('common:pagination.showing', { 
                    start: range[0], 
                    end: range[1], 
                    total 
                  })
              }}
              headerActions={
                <Space>
                  <Input
                    className="search-input"
                    placeholder={t('common:searchPlaceholder', { entity: t('apartmentUnits.title') })}
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    prefix={<SearchOutlined />}
                    style={{ width: 300 }}
                    allowClear
                  />
                  <Select
                    placeholder={t('apartmentUnits.fields.block')}
                    value={selectedBlock}
                    onChange={setSelectedBlock}
                    onOpenChange={setBlocksDropdownOpen}
                    allowClear
                    style={{ width: 150 }}
                    options={uniqueBlocks}
                  />
                  <Select
                    placeholder={t('apartmentUnits.fields.status')}
                    value={selectedStatus}
                    onChange={setSelectedStatus}
                    allowClear
                    style={{ width: 150 }}
                    options={[
                      { value: 'vacant', label: t('apartmentUnits.status.vacant') },
                      { value: 'occupied', label: t('apartmentUnits.status.occupied') },
                      { value: 'maintenance', label: t('apartmentUnits.status.maintenance') }
                    ]}
                  />
                  <Button className="add-record-button" type="primary" onClick={handleCreate} icon={<PlusOutlined />}>
                    {t('apartmentUnits.actions.add')}
                  </Button>
                </Space>
              }
            />
          </Card>
        </>
      )}
      
      <ApartmentUnitFormModal
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        onSubmit={handleSubmit}
        initialValues={selectedApartmentUnit}
        title={formMode === 'create' ? t('apartmentUnits.actions.add') : t('apartmentUnits.actions.edit')}
      />

      <DeleteConfirmationModal
        visible={showDeleteModal}
        onCancel={() => {
          setShowDeleteModal(false);
          setDeleteError(undefined);
        }}
        onConfirm={() => {
          if (unitToDelete) {
            deleteMutation.mutate(unitToDelete.id);
          }
        }}
        title={t('properties:apartmentUnits.messages.deleteConfirm')}
        error={deleteError}
        onErrorDismiss={() => setDeleteError(undefined)}
      />
    </div>
  );
};